import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Button from '@/components/Button'
import GlassCard from '@/components/GlassCard'
import { decodeCode } from '@/lib/code'

import styles from './verify.module.scss'

/** `1 234` with a narrow no-break space. */
function groupDigits(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
}

export default function VerifyScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [checked, setChecked] = useState(false)

  const trimmed = code.trim()
  // Live feedback: a valid code lights up green the moment it is complete,
  // "invalid" only appears once the host actually asked for a check.
  const score = trimmed === '' ? null : decodeCode(trimmed)
  const showInvalid = checked && trimmed !== '' && score === null

  const check = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setChecked(true)
  }

  return (
    <div className={styles.screen}>
      <GlassCard className={styles.panel}>
        <h1 className={styles.title}>{t('verify.title')}</h1>
        <p className={styles.subtitle}>{t('verify.subtitle')}</p>

        <form
          className={styles.form}
          onSubmit={check}
          noValidate
        >
          <input
            className={styles.input}
            type='text'
            value={code}
            autoComplete='off'
            autoCorrect='off'
            autoCapitalize='characters'
            spellCheck={false}
            enterKeyHint='go'
            maxLength={16}
            placeholder={t('verify.placeholder')}
            aria-label={t('verify.placeholder')}
            onChange={(event) => {
              setCode(event.target.value.toUpperCase())
              setChecked(false)
            }}
          />
          <Button type='submit'>{t('verify.check')}</Button>
        </form>

        {score !== null && (
          <div className={`${styles.result} ${styles.valid}`}>
            <span className={styles.resultTitle}>{t('verify.valid')}</span>
            <span className={styles.resultScore}>
              {t('verify.scoreLabel', { score: groupDigits(score) })}
            </span>
          </div>
        )}

        {showInvalid && (
          <div className={`${styles.result} ${styles.invalid}`}>
            <span className={styles.resultTitle}>{t('verify.invalid')}</span>
          </div>
        )}

        <Button
          variant='ghost'
          className={styles.back}
          onClick={() => navigate('/')}
        >
          {t('common.back')}
        </Button>
      </GlassCard>
    </div>
  )
}
